import React from "react";
import { motion } from "framer-motion";
import {
  Users,
  Award,
  Wallet,
  Zap,
  Sparkles,
  ShieldCheck,
  MessagesSquare,
  TrendingUp,
} from "lucide-react";
import TID from "@/lib/testIds";

const features = [
  { key: "expert-team", title: "Expert Team", desc: "Certified engineers and growth strategists.", icon: Users, span: "md:col-span-2" },
  { key: "best-practices", title: "Industry Best Practices", desc: "OWASP, ISO and SOC-aligned methodologies.", icon: Award, span: "md:col-span-1" },
  { key: "affordable", title: "Affordable Pricing", desc: "Premium quality without enterprise bloat.", icon: Wallet, span: "md:col-span-1" },
  { key: "fast-delivery", title: "Fast Delivery", desc: "Quick turnarounds without sacrificing depth.", icon: Zap, span: "md:col-span-2" },
  { key: "custom", title: "Custom Solutions", desc: "Tailored playbooks for your unique stack.", icon: Sparkles, span: "md:col-span-1" },
  { key: "security-standards", title: "Latest Security Standards", desc: "We stay ahead of the threat landscape.", icon: ShieldCheck, span: "md:col-span-1" },
  { key: "transparent", title: "Transparent Communication", desc: "Weekly reports. Always-on Slack & WhatsApp.", icon: MessagesSquare, span: "md:col-span-1" },
  { key: "results-driven", title: "Results Driven Marketing", desc: "Optimized for ROAS, leads, and revenue.", icon: TrendingUp, span: "md:col-span-2" },
];

export const WhyUs = () => {
  return (
    <section
      id="why-us"
      data-testid={TID.whyUsSection}
      className="relative py-24 md:py-32 bg-gradient-to-b from-slate-50 to-white dark:from-[#0B1120] dark:to-[#0B1120]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="max-w-3xl">
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-sm font-semibold text-brand-purple uppercase tracking-[0.18em]"
          >
            Why choose us
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900 dark:text-white"
          >
            An agency built on{" "}
            <span className="text-gradient-brand">craft and accountability</span>
          </motion.h2>
        </div>

        <div className="mt-14 grid md:grid-cols-4 gap-5 md:gap-6">
          {features.map((f, i) => {
            const Icon = f.icon;
            return (
              <motion.div
                key={f.key}
                data-testid={TID.whyUsCard(f.key)}
                initial={{ opacity: 0, y: 22 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ delay: i * 0.06, duration: 0.5 }}
                whileHover={{ y: -4 }}
                className={`group ${f.span} relative rounded-3xl p-7 bg-white dark:bg-[#0F172A]/80 border border-slate-200 dark:border-white/10 shadow-elevated transition-shadow hover:shadow-[0_30px_80px_-20px_rgba(124,58,237,0.25)]`}
              >
                <div className="flex items-center gap-3">
                  <span className="w-11 h-11 rounded-xl inline-flex items-center justify-center bg-gradient-to-br from-brand-blue to-brand-purple text-white">
                    <Icon className="w-5 h-5" />
                  </span>
                  <h3 className="font-display text-lg font-semibold text-slate-900 dark:text-white">
                    {f.title}
                  </h3>
                </div>
                <p className="mt-3 text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
                  {f.desc}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default WhyUs;
